
export interface DateRange {
  start: string;
  end: string;
}

export type StandardPeriodFilter = "this-month" | "last-month" | "custom";
export type RelativeMonthFilter = "this-month" | "last-month";

// Shared date utilities: parse date strings in a local-safe way and helpers
export function parseDateLocal(s: string | undefined | null): Date {
  if (!s) return new Date(NaN);
  // Match YYYY-MM-DD
  const dateOnly = /^\s*(\d{4})-(\d{2})-(\d{2})\s*$/.exec(s);
  if (dateOnly) {
    const y = Number(dateOnly[1]);
    const m = Number(dateOnly[2]) - 1;
    const d = Number(dateOnly[3]);
    return new Date(y, m, d);
  }
  // Match YYYY-MM-DDTHH:MM:SS (no timezone) -> treat as local
  const dateTimeLocal = /^\s*(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2}):(\d{2})(?:\.\d+)?\s*$/.exec(s);
  if (dateTimeLocal) {
    const y = Number(dateTimeLocal[1]);
    const m = Number(dateTimeLocal[2]) - 1;
    const d = Number(dateTimeLocal[3]);
    const hh = Number(dateTimeLocal[4]);
    const mm = Number(dateTimeLocal[5]);
    const ss = Number(dateTimeLocal[6]);
    return new Date(y, m, d, hh, mm, ss);
  }
  // Fallback to Date constructor (handles ISO with TZ)
  return new Date(s as string);
}

export function toLocalYMD(date: Date): string {
  if (Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function toDateInputValue(value: string | undefined | null): string {
  if (!value) return "";
  const datePrefix = /^\s*(\d{4}-\d{2}-\d{2})/.exec(value);
  return datePrefix?.[1] ?? toLocalYMD(parseDateLocal(value));
}

export function buildMonthRange(baseDate: Date): DateRange {
  const year = baseDate.getFullYear();
  const month = baseDate.getMonth();

  return {
    start: toLocalYMD(new Date(year, month, 1)),
    end: toLocalYMD(new Date(year, month + 1, 0)),
  };
}

export function buildRelativeMonthRange(monthOffset: number, baseDate: Date = new Date()): DateRange {
  return buildMonthRange(new Date(baseDate.getFullYear(), baseDate.getMonth() + monthOffset, 1));
}

/** `<input type="month">` hands back "YYYY-MM"; anything else falls back to the base month. */
export function resolveMonthInputRange(value: string | null | undefined, baseDate: Date = new Date()): DateRange {
  const match = /^(\d{4})-(\d{2})$/.exec(value ?? "");
  if (!match) return buildMonthRange(baseDate);

  const month = Number(match[2]);
  if (month < 1 || month > 12) return buildMonthRange(baseDate);

  return buildMonthRange(new Date(Number(match[1]), month - 1, 1));
}

export function toMonthInputValue(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function normalizeDateRange(start: string, end: string): DateRange {
  return start <= end ? { start, end } : { start: end, end: start };
}

export function resolveStandardPeriodRange(
  period: StandardPeriodFilter,
  customStart: string,
  customEnd: string,
  baseDate: Date = new Date(),
): DateRange {
  if (period === "this-month") return buildRelativeMonthRange(0, baseDate);
  if (period === "last-month") return buildRelativeMonthRange(-1, baseDate);
  if (!customStart || !customEnd) return buildRelativeMonthRange(0, baseDate);
  return normalizeDateRange(customStart, customEnd);
}

export function resolveRelativeMonthRange(
  period: RelativeMonthFilter,
  baseDate: Date = new Date(),
): DateRange {
  return buildRelativeMonthRange(period === "last-month" ? -1 : 0, baseDate);
}

export function localTodayYMD(): string {
  const now = new Date();
  return toLocalYMD(now);
}
